import { BCMSRecord, BCMSStateManagerCRUD, DisabledPrompt, useBCMSContext, useModel, useStateManager, useUIState } from "@bcm-dev/bcms-vite";
import Classes, { Account } from "model";
import React from "react";

import CustomSelect, { CustomSelectItem } from "components/custom-ui/CustomSelect";
import { CenterContentContainer, ContentHeaderView, PageContainer, PageContentView, TableBody, TableColumnContainer, TableColumnView, TableItemContainer, TableItemTextView, TableItemView, TableView } from "components/shared";
import { PrimaryButton, SecondaryButton } from "components/ui";

import { InfoBanner } from "components/shared/views/ConfirmBannerView";
import CustomContentHeaderWidget from "components/shared/views/CustomContentHeaderWidget";
import "./styles.css";
import "./mobile.css";


/**
 * @function StatementPreferencePage
 * @return {React.Component}
 * @exported true
 * @description The main component for StatementPreferencePage
 * @example
 * <StatementPreferencePage />
 */
export default function StatementPreferencePage(props) {
    const globalController = useBCMSContext();
    const { branding } = useModel();
    BCMSRecord.globalControllerFor(Classes, globalController);

    const data = { ...getDefaultPageData(branding), ...props.data ?? {} };
    const ui = { ...getDefaultPageUI(), ...props.ui ?? {} };
    const className = props.className?(" "+props.className):"";

    const controller = (state, setState) => ({
        data: state.data,
        ui: state.ui,

        onUpdate: id => value => {
            const accounts = state.data.accounts.map(item => {
                if(item.id === id) { item.statementPreference = value; }
                return item;
            }, []);

            BCMSStateManagerCRUD.init(state)
                .update([ "data", "accounts" ], accounts)
                .submit(setState);
        },

        onCancel: evt => {
            BCMSStateManagerCRUD.init(state)    
                .update([ "data", "accounts" ], Account.initAll())
                .submit(setState);
        }
    });

    return (
        <PageContainer className={"statement-preference-page"+className} state={ { data, ui } } controller={ controller }>
            <StatementPreferenceContent />
        </PageContainer>
    );
}

export const getDefaultPageData = (branding) => {
    const options = branding ? branding.options : {};
    
    // Returns a selected statement preference for a particular account. 
    // This is tied to branding and the index of the account in the array of accounts.
    const getStatementPreference = (account, index, options) => {
        let accountType = account.type.toLowerCase();
        const isPersonal = account.isBusiness !== "true";
        const isBusiness = account.isBusiness === "true";

        if(accountType === "cod" && isPersonal) {
            accountType = "cd";
        } else if(accountType === "cod" && isBusiness) {
            accountType = "CD";
        }
        
        const brandingOptionPrefix = `${ isBusiness ? "business": "personal" }_${ accountType }_delivery_options_abi22`;
        let selectedDelivery = "";
        const allAccountDeliveryOptions = getAccountDeliveryOptionsMap(options);
        const currAccountDeliveryOptions = allAccountDeliveryOptions[brandingOptionPrefix];

        if(index === 0) {
            for(let i = 0; i < currAccountDeliveryOptions.length; i++) {
                const curr = currAccountDeliveryOptions[i];
                const label = curr.label;
                if(label === "Paper") {
                    selectedDelivery = "Paper";
                    break;
                } else if(label === "Other") {
                    selectedDelivery = "Other";
                    break;
                }
            }
        } else {
            for(let i = 0; i < currAccountDeliveryOptions.length; i++) {
                const curr = currAccountDeliveryOptions[i];
                const label = curr.label;
                if(label !== "Paper") {
                    selectedDelivery = label;
                    break;
                }
            }
        }

        return selectedDelivery;
    };

    // A multiple values branding option with "Paper" is a special case where the first select 
    // will be set to "Paper" and the rest of the selects will
    // be set to the next value after "Paper" in the multiple values option.
    // If "Paper" is not the first value in the array of values, 
    // then the rest of the selects will be set to the first value in the array.
    /* 
    Example 1:
    For first select, avail options are "Paper -> Option 2 -> Option 3" ... "Paper" would be selected.
    For second/third/fourth selects, avail "options are Paper -> Option 2 -> Option 3" ... "Option 2" would be selected.

    Example 2:
    For first select, avail options are "Option 1 -> Option 2 -> Paper" ... "Paper" would be selected.
    For second/third/fourth selects, avail options are "Option 1 -> Option 2 -> Paper" ... "Option 1" would be selected.
    */
    const accounts = Account.initAll().reduce((acc, item, index) => {
        item.statementPreference = getStatementPreference(item, index, options);
        item.currIndex = index;
        acc.push(item);
        return acc;
    }, []);

    return {
        accounts,
        brandingOptions: options,
        // Returns the associated delivery branding option from a particular account.
        // So "Personal Checking" maps to "personal_checking_delivery_optioins_abi22".
        getBrandingOptionPrefix: (account) => {
            let accountType = account.type.toLowerCase();
            const isPersonal = account.isBusiness !== "true";
            const isBusiness = account.isBusiness === "true";

            if(accountType === "cod" && isPersonal) {
                accountType = "cd";
            } else if(accountType === "cod" && isBusiness) {
                accountType = "CD";
            }
            
            return `${ isBusiness ? "business": "personal" }_${ accountType }_delivery_options_abi22`;
        },
        // Get an object that maps a branding option with an array of custom selects.
        /* Example:
        { 
        "personal_checking_delivery_options_abi22": [{
                                label: "Paper",
                                index: 0
                            }]
        }
        */
        accountDeliveryOptionsMap: getAccountDeliveryOptionsMap(options)
    };
};

export const getDefaultPageUI = () => ({
    savedBanner: { isActive: false }
});

/**
 * @function getAccountDeliveryBrandingOptionsMap
 * @param {Object} options - The demo branding options.
 * @return {React.Component}
 * @exported false
 * @description Get the branding option values which will be used to build the account custom selects.
 * @example
 * "personal_checking_delivery_options_abi22": "Paper,Electronic"
 */
// Get the branding option values which will be used to build the account custom selects.
function getAccountDeliveryBrandingOptionsMap(options) {
    const brandingOptionNames = [
        "personal_checking_delivery_options_abi22",
        "personal_savings_delivery_options_abi22",
        "personal_loan_delivery_options_abi22",
        "personal_loc_delivery_options_abi22",
        "personal_cd_delivery_options_abi22",
        "business_checking_delivery_options_abi22",
        "business_savings_delivery_options_abi22",
        "business_loc_delivery_options_abi22",
        "business_CD_delivery_options_abi22",
        "business_loan_delivery_options_abi22"
    ];

    let accountDeliveryOptionsMap = {};

    for(let i = 0; i < brandingOptionNames.length; i++) {
        const name = brandingOptionNames[i];
        const value = options[name] === "undefined" || options[name] === undefined ? "Default" : options[name];
        accountDeliveryOptionsMap[name] = value;
    }

    return accountDeliveryOptionsMap;
};

/**
 * @function getAccountDeliveryOptionsMap
 * @param {Object} options - The demo branding options.
 * @return {React.Component}
 * @exported false
 * @description Get an object that maps a branding option with an array of custom selects.
 * @example
 * { 
"personal_checking_delivery_options_abi22": [{
                        label: "Paper",
                        index: 0
                    }]
}
 */
function getAccountDeliveryOptionsMap(options) {
    const deliveryOptionsBranding = options.delivery_options_abi22 === "undefined" || options.delivery_options_abi22 === undefined
    ? "Paper,Electronic"
    : options.delivery_options_abi22;
    let deliveryOptions = deliveryOptionsBranding.split(",");

    if(deliveryOptions.length === 1) {
        deliveryOptions = [ "Other", ...deliveryOptions ];
    }
    
    deliveryOptions = deliveryOptions.reduce((acc, option, index) => {
        acc.push({
            label: option,
            index: index
        });
        return acc;
    }, []);

    const accountDeliveryBrandingOptionsMap = getAccountDeliveryBrandingOptionsMap(options);
    let map = {};
    let account = Account.initAll()[0];
    let accountType = account.type.toLowerCase();
    const isPersonal = account.isBusiness !== "true";
    const isBusiness = account.isBusiness === "true";

    if(accountType === "cod" && isPersonal) {
        accountType = "cd";
    } else if(accountType === "cod" && isBusiness) {
        accountType = "CD";
    }
    
    const brandingOptionPrefixForFirstAccount = `${ isBusiness ? "business": "personal" }_${ accountType }_delivery_options_abi22`;

    Object.keys(accountDeliveryBrandingOptionsMap).forEach(brandingOptionName => {
        const value = accountDeliveryBrandingOptionsMap[brandingOptionName];
        let arrOfOptions = value.split(",");

        if(value === "Default") {
            arrOfOptions = deliveryOptions;

            // Only add the "Other" option to the first account select.
            if(brandingOptionName !== brandingOptionPrefixForFirstAccount) {
                arrOfOptions = arrOfOptions.filter(curr => {
                    return curr.label !== "Other";
                });
            }
        } else {
            arrOfOptions = arrOfOptions.reduce((acc, option, index) => {
                let hasOneOptionOnly = arrOfOptions.length === 1;
                // If this is the first account select and we only have 1 possible option, like "Electric",
                // then add "Other" as another possible option.
                if(hasOneOptionOnly && (brandingOptionName === brandingOptionPrefixForFirstAccount)) {
                    acc.push({
                        label: "Other",
                        index: 0
                    });
                    acc.push({
                        label: option,
                        index: 1
                    });
                } else {
                    acc.push({
                        label: option,
                        index: index
                    });
                }
                
                return acc;
            }, []);
        }

        map[brandingOptionName] = arrOfOptions;
    });

    return map;
};

function StatementPreferenceContent(props) {
    const { data, ui, onUpdate, onCancel } = useStateManager();
    const { branding } = useModel();
    const { options } = branding;
    const accountItems = data.accounts.map((account, index) => {
        return <StatementPreferenceTableRow key={ `Account_${ account.id }` } account={ account } accountIndex={ index } onChange={ onUpdate(account.id) } /> 
    }, [])
    const subHeaderContent = options.statement_preference_subheader_abi22; 
    const promoText = options.statement_preference_promo_abi22;
    const subfooterText = options.statementpreference_subfooter_abi22;

    return (
        <PageContentView>
            {/* InfoBanner is only shown in captures NOT in click-thru */}
            { ui.savedBanner.isActive && <InfoBanner text="Your statement preference has been saved." dataPoint="Statement_Preference_Banner" /> }
            <div className="flex-container flex-vertical page-widget-wrapper">
                {promoText && <CustomContentHeaderWidget content = {promoText}/>}
                <CenterContentContainer>
                    <ContentHeaderView text="Statement Preference" />
                    {subHeaderContent && <CustomContentHeaderWidget content = {subHeaderContent}/>}
                    <TableView className="statement-preference-table">
                        <TableColumnContainer>
                            <TableColumnView className="text-left account-nickname-column" text="Account Nickname" />
                            <TableColumnView className="text-left account-number-column" text="Account Number" />
                            <TableColumnView className="text-left delivery-option-column" text="Delivery Option" />
                        </TableColumnContainer>
                        
                        <TableBody>
                            { accountItems }
                        </TableBody>
                    </TableView>
                    
                    <div className="flex-container flex-horizontal align-center button-container">
                        <SecondaryButton text="Cancel" onClick={ onCancel } />

                        <DisabledPrompt>
                            <PrimaryButton text="Save" dataPoint="Statement_Preference_Save" />
                        </DisabledPrompt>
                    </div>
                </CenterContentContainer>
                { subfooterText && <CustomContentHeaderWidget content={ subfooterText } /> }
            </div>
        </PageContentView>
    );
}

/**
 * @function StatementPreferenceTableRow
 * @param {Account} account - The account use to fill out information
 * @return {React.Component}
 * @exported false
 * @description The StatementPreferenceTableRow is one row in the table shown on  StatementPreferencePage.
 * @example
 * <StatementPreferenceTableRow account={ account } />
 */

function StatementPreferenceTableRow(props) {
    const { data } = useStateManager();
    const account = props.account ?? Account.firstInstanceOf();
    const { key, value } = getStatementPreferenceItem(account.id);
    const { config } = useUIState(key, value);

    const onChange = value => props.onChange instanceof Function
    ? props.onChange(value)
    : undefined;
    
    // If the demo "Other" option is set, only show that option for the first account select 
    // but not the rest of the account selects.
    //if(account.currIndex > 0 && includesDemoDefaultOption) options = options.slice(1);
    const brandingOptionPrefix = data.getBrandingOptionPrefix(account);
    const deliveryOptions = data.accountDeliveryOptionsMap[brandingOptionPrefix];
    
    const items = deliveryOptions.reduce((acc, option) => {
        acc.push(<CustomSelectItem key={ option.index } dataPoint={ `Preference_${ account.id }_${ option.index }` }>{ option.label }</CustomSelectItem>);
        return acc;
    }, []);

    return (
    <TableItemContainer>
        <TableItemTextView className="account-nickname-column" text={account.nickname} />
        <TableItemTextView className="account-number-column" text={account.number} />
        <TableItemView className="delivery-option-column">
            <CustomSelect value={ account.statementPreference } active={ config.selectIsActive } onChange={onChange} dataPoint={ `Preferece_Select_${ account.id }` }>
                { items }
            </CustomSelect>
        </TableItemView>
    </TableItemContainer>
    );
}

export const getStatementPreferenceItem = id => ({
  key: `Preference_${ id }`,
  value: {
    selectIsActive: false
  }
});